import { transition } from '@helpers/animate';
import brand, { BrandColours } from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { InterfaceIcon } from '@helpers/icons';
import { Device, until } from '@helpers/media';
import srOnly from '@helpers/srOnly';
import { BackgroundVariant } from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import { rgba } from 'polished';
import styled, { css } from 'styled-components';
export const CommonButtonTextStyles = css `
  ${transition('color', 0.25)};
  ${fonts.lato.bold};

  color: currentColor;
  font-size: var(--fontSize);
  line-height: 1.5;
  text-decoration: none;
`;
const Text = styled.span `
  ${CommonButtonTextStyles};

  [data-icon-only='true'] & {
    ${srOnly};
  }
`;
const IconWrapper = styled.div `
  ${transition('color', 0.25)};

  --iconHeight: 24px;

  display: grid;
  place-content: center;
  color: currentColor;
  height: var(--iconHeight);
  aspect-ratio: 1;
  margin: 0;
  padding: 0;

  > svg {
    display: block;
    height: var(--iconHeight);
  }

  [data-icon-position='left'] & {
    order: -1;
  }

  [data-icon-position='right'] & {
    order: 1;
  }

  [data-icon-type=${InterfaceIcon.ArrowRight}] & {
    ${transition('transform, color', 0.25)};
  }

  [data-icon-type=${InterfaceIcon.Close}] & {
    --iconHeight: 12px;
  }

  [data-icon-position='left'][data-icon-type=${InterfaceIcon.ArrowRight}] & {
    svg {
      transform: scale(-1);
    }
  }
`;
const IconHoverStyles = css `
  &[data-icon-type=${InterfaceIcon.ArrowRight}] {
    ${IconWrapper} {
      transform: translateX(4px);
    }
  }

  &[data-icon-position='left'][data-icon-type=${InterfaceIcon.ArrowRight}] {
    ${IconWrapper} {
      transform: translateX(-4px);
    }
  }
`;
export const DefaultButtonStyles = css `
  ${ButtonReset};
  ${transition('background-color, color', 0.25)};

  --bgColour: ${brand.secondary.yellow.light};
  --bgColourActive: var(--bgColourHover);
  --bgColourDisabled: ${brand.grey.light};
  --bgColourHover: ${brand.secondary.yellow.dark};
  --borderColorActive: var(--bgColourActive);
  --borderColorHover: var(--bgColourHover);
  --borderColour: var(--bgColour);
  --borderColourDisabled: var(--bgColourDisabled);
  --borderRadius: 50px;
  --borderWidth: 1px;
  --buttonHeight: ${fluid(44, 64)};
  --colourText: ${brand.global.black};
  --colourTextDisabled: ${brand.grey.dark};
  --colourTextHover: var(--colourText);
  --fontSize: ${fluid(15, 16)};

  display: inline-flex;
  gap: ${fluid(8, 12)};
  align-items: center;
  border: var(--borderWidth) solid var(--borderColour);
  background: none;
  background-color: var(--bgColour);
  background-position: top center;
  color: var(--colourText);
  min-height: var(--buttonHeight);
  padding: 8px ${fluid(16, 20)};
  text-decoration: none;
  user-select: none;
  width: auto;
  border-radius: var(--borderRadius);

  /* Mobile click styles - button should mimic press */
  @media ${until(Device.TabletLarge)} {
    ${transition('background-color, color, transform', 0.15)};

    &:active {
      transform: translateY(1.5px);
    }
  }

  span& {
    .focus-ring & {
      outline: auto;
      outline-offset: 3px;
      outline-width: 2px;
    }

    .focus-ring &,
    button:hover &,
    a:hover &,
    label:hover & {
      ${IconHoverStyles};

      color: var(--colourTextHover);
      background-color: var(--bgColourHover);
      border-color: var(--borderColorHover);
    }

    button:active &,
    a:active & {
      background-color: var(--bgColourActive);
      border-color: var(--borderColorActive);
      color: var(--colourTextHover);
    }
  }

  &:not(span) {
    cursor: pointer;

    &:focus-visible,
    &.focus-ring {
      outline: auto;
      outline-offset: 3px;
      outline-width: 2px;
    }

    &:focus-visible,
    &.focus-ring,
    &:hover {
      ${IconHoverStyles};

      color: var(--colourTextHover);
      background-color: var(--bgColourHover);
      border-color: var(--borderColorHover);
    }

    &:active {
      background-color: var(--bgColourActive);
      border-color: var(--borderColorActive);
      color: var(--colourTextHover);
    }
  }

  /* Increase specificity to override :not(span) style */
  &[aria-disabled='true'][aria-disabled='true'][aria-disabled='true'],
  &:disabled:disabled:disabled {
    color: var(--colourTextDisabled);
    background-color: var(--bgColourDisabled);
    border-color: var(--borderColourDisabled);

    cursor: not-allowed;

    ${IconWrapper} {
      transform: none;
    }
  }
`;
export const SecondaryButtonStyles = css `
  --bgColour: transparent;
  --bgColourActive: ${brand.primary.purple.dark};
  --bgColourDisabled: transparent;
  --bgColourHover: var(--colourText);
  --borderColorActive: var(--bgColourActive);
  --borderColorHover: var(--bgColourHover);
  --borderColour: var(--colourText);
  --borderColourDisabled: var(--colourTextDisabled);
  --colourText: ${brand.primary.purple.light};
  --colourTextDisabled: ${brand.grey.dark};
  --colourTextHover: ${brand.global.white};

  [data-variant=${BrandColours.OrangeMid}] &,
  [data-variant=${BrandColours.OrangeDark}] &,
  [data-variant=${BrandColours.GreenDark}] &,
  [data-variant=${BrandColours.PinkDark}] &,
  [data-variant=${BackgroundVariant.Blue}] &,
  [data-variant=${BackgroundVariant.Purple}] &,
  [data-variant=${BackgroundVariant.PurpleDark}] & {
    --bgColourActive: ${brand.primary.grey};
    --colourText: ${brand.global.white};
  }

  [data-variant=${BrandColours.OrangeDark}] &,
  [data-variant=${BrandColours.GreenDark}] &,
  [data-variant=${BackgroundVariant.Blue}] &,
  [data-variant=${BrandColours.PinkDark}] &,
  [data-variant=${BackgroundVariant.PurpleDark}] & {
    --colourTextDisabled: ${brand.grey.light};
  }

  [data-variant=${BackgroundVariant.Purple}] & {
    --colourTextDisabled: ${brand.grey.lightest};
  }

  [data-variant=${BackgroundVariant.Purple}] &,
  [data-variant=${BackgroundVariant.PurpleDark}] & {
    --colourTextHover: ${brand.primary.purple.light};
  }

  [data-variant=${BackgroundVariant.Blue}] & {
    --colourTextHover: ${brand.primary.blue.dark};
  }

  [data-variant=${BrandColours.GreenDark}] & {
    --colourTextHover: ${brand.secondary.green.dark};
  }

  [data-variant=${BrandColours.OrangeDark}] & {
    --colourTextHover: ${brand.secondary.orange.dark};
  }

  [data-variant=${BrandColours.OrangeMid}] & {
    --colourTextHover: ${brand.secondary.orange.mid};
  }

  [data-variant=${BrandColours.PinkDark}] & {
    --colourTextHover: ${brand.secondary.pink.mid};
  }

  [data-variant=${BrandColours.BlueLight}] &,
  [data-variant=${BrandColours.GreenLight}] &,
  [data-variant=${BrandColours.OrangeLight}] &,
  [data-variant=${BrandColours.PinkLight}] & {
    --colourText: ${brand.global.black};
  }

  [data-variant=${BrandColours.BlueLight}] & {
    --bgColourActive: ${brand.primary.blue.dark};
  }

  [data-variant=${BrandColours.GreenLight}] & {
    --bgColourActive: ${brand.secondary.green.dark};
  }

  [data-variant=${BrandColours.OrangeLight}] & {
    --bgColourActive: ${brand.secondary.orange.dark};
  }

  [data-variant=${BrandColours.PinkLight}] & {
    --bgColourActive: ${brand.secondary.pink.dark};
  }
`;
export const TertiaryButtonStyles = css `
  --bgColour: ${brand.primary.purple.light};
  --bgColourActive: ${brand.grey.lightest};
  --bgColourDisabled: ${brand.grey.grey40};
  --bgColourHover: transparent;
  --borderColorActive: var(--bgColour);
  --borderColorHover: var(--bgColour);
  --borderColour: var(--colourText);
  --borderColourDisabled: var(--bgColourDisabled);
  --colourText: ${brand.global.white};
  --colourTextDisabled: ${brand.grey.lightest};
  --colourTextHover: var(--bgColour);

  [data-variant=${BackgroundVariant.Blue}] &,
  [data-variant=${BackgroundVariant.Purple}] &,
  [data-variant=${BackgroundVariant.PurpleDark}] & {
    --bgColour: ${brand.global.white};
    --colourText: ${brand.primary.purple.light};
    --bgColourActive: ${rgba(brand.global.white, 0.2)};
  }

  [data-variant=${BackgroundVariant.Grey}] & {
    --bgColourActive: ${rgba(brand.global.black, 0.1)};
  }
`;
const Container = styled.button `
  ${DefaultButtonStyles};

  &[data-type='secondary'] {
    ${SecondaryButtonStyles};
  }

  &[data-type='tertiary'] {
    ${TertiaryButtonStyles};
  }

  &[data-icon-only='true'] {
    height: var(--buttonHeight);
    justify-content: center;
    padding: 0;
    width: var(--buttonHeight);
  }
`;
const ButtonStyles = {
    Container,
    IconWrapper,
    Text,
};
export default ButtonStyles;
